import * as React from 'react';
import { DotStatus, HeaderFilterTable, Icon } from '@/components/common';
import { Options } from '@/components/ui/combobox';
import Icons from '@/const/icons';
import { GET_CLIENT_DIRECTORY_ACTION_FILTER } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { clientDirectoryService } from '@/api/clientDirectory';
import { ActionStatus, ClientDirectoryFiltersType } from '@/interfaces';
import { useFilters } from '@/hooks';
import { GC_TIME, STALE_TIME } from '@/const/common';

interface TasksFilterProps {
	title: string;
	filterKey: ClientDirectoryFiltersType;
	totalFilterKey: ClientDirectoryFiltersType;
}

export const getNameFormatted = (value: ActionStatus) => {
	const names: Record<ActionStatus, string> = {
		DUE_SOON: 'Due Soon',
		PAST_DUE: 'Past Due',
		PENDING: 'Pending',
	};
	return names[value];
};

export const getIcon = (value: ActionStatus) => {
	const icons: Record<ActionStatus, JSX.Element> = {
		DUE_SOON: <DotStatus color="orange" />,
		PAST_DUE: <DotStatus color="red" />,
		PENDING: <DotStatus color="yellow" />,
	};
	return icons[value];
};

export const TasksFilter: React.FC<TasksFilterProps> = ({
	title,
	filterKey,
	totalFilterKey,
}) => {
	const { setParamSeveralFilters, hasFilter } = useFilters();
	const [data, setData] = React.useState<Options[]>([]);

	const dataDefined: Options[] = [
		{
			label: '0 Tasks',
			value: '0',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
		{
			label: '1 Task',
			value: '1',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
		{
			label: '+2 Tasks',
			value: '2',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
	];

	const { data: actionFilterList } = useQuery({
		refetchOnWindowFocus: false,
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
		queryKey: [GET_CLIENT_DIRECTORY_ACTION_FILTER],
		queryFn: () => clientDirectoryService.getActionFilter(),
	});

	React.useEffect(() => {
		if (actionFilterList) {
			const auxList: Options[] = actionFilterList.data
				.map((item) => ({
					label: getNameFormatted(item),
					value: item,
					icon: getIcon(item),
				}))
				.reverse();
			setData(dataDefined.concat(auxList));
		}
	}, [actionFilterList]);

	const handleSelectFilters = (value?: string) => {
		// specials filters
		switch (value) {
			case '0':
			case '1':
			case '2':
				if (value === '0' || value === '1' || value === '2') {
					setParamSeveralFilters([
						{ label: filterKey, value: '' },
						{ label: totalFilterKey, value: value },
					]);
				}
				break;

			default:
				setParamSeveralFilters([
					{ label: filterKey, value: value },
					{ label: totalFilterKey, value: '' },
				]);
				break;
		}
	};

	const handleClearAll = () => {
		setParamSeveralFilters([
			{ label: filterKey, value: '' },
			{ label: totalFilterKey, value: '' },
		]);
	};

	return (
		<HeaderFilterTable
			title={title}
			searchTitle="Tasks"
			filterOptions={data}
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			hasFilter={hasFilter(filterKey) || hasFilter(totalFilterKey)}
			classNamePopoverContent="lg:w-[262px]"
		/>
	);
};
